import { useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { useAuth } from "@/contexts/AuthContext";
import { getLanguageFromLocale } from "@/utils/locale";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  BookOpen, ChevronDown, ChevronRight, Store, ShoppingBag, CreditCard,
  Printer, QrCode, Truck, Users, BarChart3, Settings, Bot, Globe,
  ChefHat, Grid3X3, Megaphone, Shield, Smartphone, Monitor, Clock,
  Package, Sparkles, Map, MessageSquare, KeyRound, Receipt
} from "lucide-react";

const T = {
  pt: {
    title: "Guia Completo",
    subtitle: "Tudo que voce precisa saber para usar o Kaiso System",
    sections: [
      {
        id: "primeiros-passos",
        icon: Store,
        title: "Primeiros Passos",
        color: "#10b981",
        items: [
          {
            q: "Como configurar meu restaurante?",
            a: "Va em **Configuracoes** no menu lateral. Na aba **Geral**, preencha: nome do restaurante, endereco, telefone, email e CIF. Clique em Salvar. Depois va na aba **Personalizacao** para adicionar logo, cores do tema e banner."
          },
          {
            q: "Como adicionar meu cardapio?",
            a: "Va em **Cardapio** no menu lateral. Clique em **+ Categoria** para criar categorias (ex: Entradas, Pratos Principais, Bebidas). Depois clique em **+ Produto** dentro de cada categoria. Preencha nome, preco, descricao e foto. Voce pode usar a **IA** para gerar descricoes e fotos automaticamente!"
          },
          {
            q: "Como configurar horarios de funcionamento?",
            a: "Va em **Configuracoes > Horarios**. Defina os horarios de abertura e fechamento para cada dia da semana. O sistema ira mostrar automaticamente se o restaurante esta aberto ou fechado para os clientes."
          },
          {
            q: "Como configurar formas de pagamento?",
            a: "Va em **Configuracoes > Pagamentos**. Ative os metodos que aceita: Cartao (Stripe), MBWay, Bizum, Dinheiro. Para pagamento online, voce precisa configurar as chaves do Stripe em **Integracoes**."
          },
          {
            q: "Como personalizar o tema/cores do meu menu?",
            a: "Va em **Configuracoes > Personalizacao**. Escolha as cores primaria, de fundo, do cabecalho e do texto. O preview mostra como ficara em tempo real. Dica: para um visual moderno, use fundo escuro (#0a0a0a) com texto claro."
          }
        ]
      },
      {
        id: "pos-caixa",
        icon: Monitor,
        title: "POS / Caixa",
        color: "#6366f1",
        items: [
          {
            q: "Como fazer uma venda no caixa?",
            a: "Va em **POS** no menu lateral. Clique em **Venda Direta**. Selecione os produtos, ajuste quantidades e clique no metodo de pagamento (Cartao, Dinheiro, etc). A venda sera registrada automaticamente."
          },
          {
            q: "Como dividir a conta entre pessoas?",
            a: "Apos registrar a venda, na tela de sucesso clique em **DIVIDIR CONTA**. Selecione quais itens cada pessoa vai pagar e o metodo de pagamento de cada um. O sistema cria sub-pedidos separados."
          },
          {
            q: "Como imprimir recibos automaticamente?",
            a: "1. Instale o **QZ Tray** no computador (baixe em qz.io)\n2. Va em **Configuracoes > Impressoras**, clique em **Conectar QZ Tray**\n3. Selecione sua impressora na lista\n4. De um alias (ex: 'caixa')\n5. Ative **Imprimir automaticamente**\n6. Salve. Agora ao registrar uma venda, clique em **IMPRIMIR RECIBO** e a impressao vai direto para a impressora, sem popup."
          },
          {
            q: "Como emitir faturas?",
            a: "Na tela de sucesso da venda, clique em **EMITIR FATURA**. Preencha o NIF do cliente (opcional) e confirme. A fatura sera gerada com numero sequencial e pode ser baixada em PDF."
          },
          {
            q: "Como ver os pagamentos do dia?",
            a: "No POS, clique em **Pagamentos**. Voce ve o total do dia separado por metodo de pagamento (cartao, dinheiro, etc)."
          }
        ]
      },
      {
        id: "mesas",
        icon: Grid3X3,
        title: "Mesas e Pedidos na Mesa",
        color: "#f59e0b",
        items: [
          {
            q: "Como criar mesas?",
            a: "Va em **Mesas** no menu lateral. Clique em **+ Mesa**. Defina o numero e nome da mesa. O sistema gera automaticamente um QR code para cada mesa."
          },
          {
            q: "Como funciona o QR code da mesa?",
            a: "Cada mesa tem um QR code unico. O cliente escaneia com o celular, o cardapio digital abre no navegador, o cliente escolhe os produtos e faz o pedido. O pedido aparece na cozinha automaticamente."
          },
          {
            q: "Como ver pedidos de uma mesa?",
            a: "Clique na mesa na tela de Mesas. Voce ve todos os pedidos da mesa, pode adicionar novos itens, dividir a conta ou transferir itens para outra mesa."
          },
          {
            q: "Como transferir itens entre mesas?",
            a: "Na tela da mesa, clique em **TRANSFERIR ITEM**. Selecione os itens e escolha a mesa de destino. Os itens serao movidos automaticamente."
          }
        ]
      },
      {
        id: "pedidos",
        icon: ShoppingBag,
        title: "Pedidos e Cozinha",
        color: "#ef4444",
        items: [
          {
            q: "Como gerenciar pedidos?",
            a: "Va em **Pedidos** no menu lateral. Os pedidos aparecem em tempo real. Clique em um pedido para ver detalhes. Mude o status: Pendente -> Preparando -> Pronto -> Entregue."
          },
          {
            q: "Como funciona o monitor de cozinha?",
            a: "Va em **Cozinha** no menu lateral. Ele mostra os pedidos em cards organizados por status. A cozinha pode marcar itens como prontos conforme vai preparando."
          },
          {
            q: "Como aceitar/rejeitar pedidos?",
            a: "Na tela de Pedidos, quando chega um novo pedido voce ouve um som de notificacao. Clique no pedido e escolha **Aceitar** ou **Rejeitar**."
          },
          {
            q: "Como configurar notificacoes sonoras?",
            a: "O sino de notificacao no canto superior direito controla o som. Clique para ativar/desativar. Voce tambem pode ativar notificacoes push no navegador."
          }
        ]
      },
      {
        id: "delivery",
        icon: Truck,
        title: "Delivery e Entregadores",
        color: "#8b5cf6",
        items: [
          {
            q: "Como ativar delivery?",
            a: "Va em **Configuracoes > Envios**. Ative a opcao de delivery. Configure as zonas de entrega com raio em km e taxa de entrega para cada zona."
          },
          {
            q: "Como cadastrar entregadores?",
            a: "Va em **Equipe** no menu lateral. Clique em **+ Entregador**. Preencha nome, telefone e email. O entregador recebera notificacoes dos pedidos."
          },
          {
            q: "Como o cliente acompanha a entrega?",
            a: "Apos o pedido ser enviado, o cliente recebe atualizacoes por **WhatsApp**: pedido aceito, em preparo, saiu para entrega, entregue. Se delivery, pode rastrear o entregador no mapa em tempo real."
          },
          {
            q: "Como definir tempo estimado de entrega?",
            a: "Na tela de Pedidos, defina o tempo de entrega no botao de minutos (20, 30, 40, 50, 60, 90 min). Isso sera comunicado ao cliente."
          }
        ]
      },
      {
        id: "clientes",
        icon: Users,
        title: "Clientes e Marketing",
        color: "#ec4899",
        items: [
          {
            q: "Como ver meus clientes?",
            a: "Va em **Clientes** no menu lateral. Veja a lista de todos os clientes que ja fizeram pedidos, com historico de compras, total gasto e frequencia."
          },
          {
            q: "Como fazer campanhas de marketing?",
            a: "Va em **Marketing** no menu lateral. Crie campanhas por WhatsApp ou email. Segmente por clientes que nao pedem ha X dias, por valor gasto, etc."
          },
          {
            q: "Como funciona o programa de fidelidade?",
            a: "Ative em **Configuracoes > Marketing**. Os clientes acumulam pontos a cada compra e podem trocar por descontos ou produtos gratis."
          }
        ]
      },
      {
        id: "financeiro",
        icon: BarChart3,
        title: "Financeiro e Relatorios",
        color: "#14b8a6",
        items: [
          {
            q: "Como ver relatorios de vendas?",
            a: "Va em **Relatorios** no menu lateral. Veja vendas por dia, semana, mes. Filtre por periodo, metodo de pagamento, canal de venda. Exporte em PDF ou Excel."
          },
          {
            q: "Como funciona o controle financeiro?",
            a: "Va em **Financeiro** no menu lateral. Veja CMV (Custo de Mercadoria Vendida), DRE (Demonstracao de Resultado), fluxo de caixa, e analise de lucratividade por produto."
          },
          {
            q: "Como configurar impostos?",
            a: "Va em **Configuracoes > Geral**. Defina a taxa de imposto (IVA) e se os precos incluem imposto ou nao."
          }
        ]
      },
      {
        id: "ia",
        icon: Bot,
        title: "Inteligencia Artificial",
        color: "#CDA34F",
        items: [
          {
            q: "Como usar a IA para criar o cardapio?",
            a: "No **Cardapio**, ao adicionar um produto, clique no botao de **IA**. Ela gera automaticamente: descricao do prato, foto profissional do prato, e sugestoes de preco baseadas no mercado."
          },
          {
            q: "Como funciona o Agente IA 24/7?",
            a: "O **Assistente IA** no menu lateral e um agente inteligente que responde duvidas, ajuda com configuracoes e da sugestoes para melhorar seu negocio. Funciona 24 horas."
          },
          {
            q: "Como funciona a IA Operacional?",
            a: "Va em **IA Operacional**. Ela analisa seus dados e sugere: precos dinamicos (desconto automatico em dias fracos), gestao de estoque, e previsao de demanda."
          },
          {
            q: "O Agente IA tambem ajuda meus clientes?",
            a: "Sim! No cardapio digital, os clientes tem acesso ao chat IA que responde sobre ingredientes, alergenos, recomendacoes e ajuda com o pedido. **Avise seus clientes para clicarem no icone do chat no menu!**"
          }
        ]
      },
      {
        id: "dominio",
        icon: Globe,
        title: "Dominio Personalizado",
        color: "#3b82f6",
        items: [
          {
            q: "Como configurar meu dominio?",
            a: "Va em **Configuracoes > Dominio**. Digite seu dominio (ex: pedido.meusushi.com). O sistema configura automaticamente via Cloudflare. Se o dominio estiver na mesma conta Cloudflare, a configuracao e instantanea."
          },
          {
            q: "Preciso fazer algo no meu provedor de dominio?",
            a: "Se o dominio estiver em outra conta, o sistema mostrara instrucoes de DNS (CNAME) que voce deve adicionar no seu provedor. A propagacao pode levar ate 48h."
          },
          {
            q: "Como verificar se o dominio esta funcionando?",
            a: "Na mesma tela de Dominio, clique em **Verificar**. O sistema checa se o dominio esta apontando corretamente. Quando estiver verde, esta pronto!"
          }
        ]
      },
      {
        id: "seguranca",
        icon: Shield,
        title: "Seguranca e Equipe",
        color: "#64748b",
        items: [
          {
            q: "Como adicionar funcionarios?",
            a: "Va em **Equipe** no menu lateral. Clique em **+ Funcionario**. Defina nome, email, senha e **funcao** (Gerente, Caixa, Cozinheiro, Garcom). Cada funcao tem permissoes diferentes."
          },
          {
            q: "Como funciona o controle de ponto?",
            a: "Va em **Ponto** no menu lateral. Os funcionarios fazem check-in/check-out pelo sistema. Voce ve as horas trabalhadas de cada um."
          },
          {
            q: "Como alterar minha senha?",
            a: "Clique no seu nome/avatar no menu lateral, depois em **Alterar Senha**. Digite a senha atual e a nova senha."
          },
          {
            q: "Como ativar autenticacao de 2 fatores?",
            a: "Va em **Configuracoes > Seguranca**. Ative o 2FA. Na proxima vez que entrar, alem da senha, precisara do codigo do app autenticador."
          }
        ]
      },
      {
        id: "integrações",
        icon: Sparkles,
        title: "Integracoes",
        color: "#a855f7",
        items: [
          {
            q: "Como integrar com WhatsApp?",
            a: "Va em **Integracoes** no menu lateral. Na secao WhatsApp, escaneie o QR code com seu celular para conectar. Os pedidos serao enviados automaticamente por WhatsApp aos clientes."
          },
          {
            q: "Como configurar Stripe para pagamentos?",
            a: "Va em **Integracoes > Stripe**. Cole sua chave publica e secreta do Stripe. Ative os metodos de pagamento desejados."
          },
          {
            q: "Preciso de ajuda tecnica?",
            a: "Entre em contato pelo email **suporte@kaisosystem.com** ou pelo **WhatsApp**. Nossa equipe esta pronta para ajudar!"
          }
        ]
      }
    ]
  },
  es: {
    title: "Guia Completa",
    subtitle: "Todo lo que necesitas saber para usar Kaiso System",
    sections: [
      {
        id: "primeros-pasos",
        icon: Store,
        title: "Primeros Pasos",
        color: "#10b981",
        items: [
          { q: "Como configurar mi restaurante?", a: "Ve a **Configuracion** en el menu lateral. En la pestana **General**, completa: nombre del restaurante, direccion, telefono, email y CIF. Haz clic en Guardar. Luego ve a **Personalizacion** para agregar logo, colores del tema y banner." },
          { q: "Como agregar mi menu?", a: "Ve a **Menu** en el menu lateral. Haz clic en **+ Categoria** para crear categorias (ej: Entrantes, Platos Principales, Bebidas). Luego haz clic en **+ Producto** dentro de cada categoria. Completa nombre, precio, descripcion y foto. Puedes usar la **IA** para generar descripciones y fotos automaticamente!" },
          { q: "Como configurar horarios?", a: "Ve a **Configuracion > Horarios**. Define los horarios de apertura y cierre para cada dia. El sistema mostrara automaticamente si el restaurante esta abierto o cerrado." },
          { q: "Como configurar formas de pago?", a: "Ve a **Configuracion > Pagos**. Activa los metodos que aceptas: Tarjeta (Stripe), Bizum, MBWay, Efectivo. Para pago online, configura las claves de Stripe en **Integraciones**." },
          { q: "Como personalizar colores del menu?", a: "Ve a **Configuracion > Personalizacion**. Elige colores primario, fondo, cabecera y texto. El preview muestra como quedara en tiempo real." }
        ]
      },
      {
        id: "pos-caja",
        icon: Monitor,
        title: "POS / Caja",
        color: "#6366f1",
        items: [
          { q: "Como hacer una venta?", a: "Ve a **POS**. Haz clic en **Venta Directa**. Selecciona productos, ajusta cantidades y haz clic en el metodo de pago." },
          { q: "Como dividir la cuenta?", a: "Tras registrar la venta, en la pantalla de exito haz clic en **DIVIDIR CUENTA**. Selecciona que items paga cada persona y el metodo de pago." },
          { q: "Como imprimir recibos?", a: "1. Instala **QZ Tray** (qz.io)\n2. Ve a **Configuracion > Impresoras**, conecta QZ Tray\n3. Selecciona tu impresora\n4. Activa impresion automatica\n5. Al hacer una venta, haz clic en **IMPRIMIR RECIBO**" },
          { q: "Como emitir facturas?", a: "En la pantalla de exito, haz clic en **EMITIR FACTURA**. Completa el NIF del cliente y confirma." },
          { q: "Como ver los pagos del dia?", a: "En POS, haz clic en **Pagamentos**. Veras el total del dia por metodo de pago." }
        ]
      },
      {
        id: "mesas",
        icon: Grid3X3,
        title: "Mesas y Pedidos",
        color: "#f59e0b",
        items: [
          { q: "Como crear mesas?", a: "Ve a **Mesas**. Haz clic en **+ Mesa**. Define numero y nombre. Se genera un QR automaticamente." },
          { q: "Como funciona el QR?", a: "Cada mesa tiene un QR unico. El cliente escanea, el menu digital se abre, elige productos y hace el pedido. Llega automaticamente a la cocina." },
          { q: "Como transferir items?", a: "En la pantalla de mesa, haz clic en **TRANSFERIR ITEM** o **TRANSFERIR MESA** para mover items o toda la cuenta." }
        ]
      },
      {
        id: "pedidos",
        icon: ShoppingBag,
        title: "Pedidos y Cocina",
        color: "#ef4444",
        items: [
          { q: "Como gestionar pedidos?", a: "Ve a **Pedidos**. Los pedidos aparecen en tiempo real. Cambia el estado: Pendiente -> Preparando -> Listo -> Entregado." },
          { q: "Como funciona el monitor de cocina?", a: "Ve a **Cocina**. Muestra pedidos organizados por estado. La cocina marca items como listos al prepararlos." }
        ]
      },
      {
        id: "delivery",
        icon: Truck,
        title: "Delivery",
        color: "#8b5cf6",
        items: [
          { q: "Como activar delivery?", a: "Ve a **Configuracion > Envios**. Activa delivery. Configura zonas de entrega con radio en km y tarifa." },
          { q: "Como registrar repartidores?", a: "Ve a **Equipo**. Haz clic en **+ Repartidor**. El repartidor recibira notificaciones de pedidos." }
        ]
      },
      {
        id: "ia",
        icon: Bot,
        title: "Inteligencia Artificial",
        color: "#CDA34F",
        items: [
          { q: "Como usar la IA para crear menu?", a: "Al agregar un producto, haz clic en **IA**. Genera automaticamente descripcion, foto profesional y sugerencias de precio." },
          { q: "Como funciona el Agente IA 24/7?", a: "El **Asistente IA** responde dudas, ayuda con configuraciones y da sugerencias. Funciona 24 horas. **Tus clientes tambien pueden usarlo desde el menu digital!**" }
        ]
      },
      {
        id: "integraciones",
        icon: Sparkles,
        title: "Integraciones y Soporte",
        color: "#a855f7",
        items: [
          { q: "Como integrar WhatsApp?", a: "Ve a **Integraciones > WhatsApp**. Escanea el QR con tu celular. Los pedidos se envian automaticamente a los clientes." },
          { q: "Necesito ayuda?", a: "Contacta por email **suporte@kaisosystem.com** o por **WhatsApp**." }
        ]
      }
    ]
  },
  en: {
    title: "Complete Guide",
    subtitle: "Everything you need to know to use Kaiso System",
    sections: [
      {
        id: "getting-started",
        icon: Store,
        title: "Getting Started",
        color: "#10b981",
        items: [
          { q: "How to set up my restaurant?", a: "Go to **Settings** in the sidebar. In the **General** tab, fill in: restaurant name, address, phone, email and tax ID. Click Save. Then go to **Customization** to add logo, theme colors and banner." },
          { q: "How to add my menu?", a: "Go to **Menu** in the sidebar. Click **+ Category** to create categories. Then **+ Product** inside each category. Fill in name, price, description and photo. You can use **AI** to auto-generate descriptions and photos!" },
          { q: "How to set business hours?", a: "Go to **Settings > Hours**. Set opening and closing times for each day. The system will automatically show if the restaurant is open or closed." },
          { q: "How to set up payments?", a: "Go to **Settings > Payments**. Enable the methods you accept: Card (Stripe), MBWay, Bizum, Cash. For online payment, configure Stripe keys in **Integrations**." },
          { q: "How to customize theme colors?", a: "Go to **Settings > Customization**. Choose primary, background, header and text colors. The preview shows how it will look in real time." }
        ]
      },
      {
        id: "pos",
        icon: Monitor,
        title: "POS / Register",
        color: "#6366f1",
        items: [
          { q: "How to make a sale?", a: "Go to **POS**. Click **Direct Sale**. Select products, adjust quantities and click the payment method." },
          { q: "How to split the bill?", a: "After the sale, click **SPLIT BILL**. Select which items each person pays and their payment method." },
          { q: "How to print receipts silently?", a: "1. Install **QZ Tray** (qz.io)\n2. Go to **Settings > Printers**, connect QZ Tray\n3. Select your printer\n4. Enable auto-print\n5. Click **PRINT RECEIPT** after a sale" },
          { q: "How to issue invoices?", a: "Click **ISSUE INVOICE** on the success screen. Fill in the customer's tax ID and confirm." }
        ]
      },
      {
        id: "tables",
        icon: Grid3X3,
        title: "Tables & Orders",
        color: "#f59e0b",
        items: [
          { q: "How to create tables?", a: "Go to **Tables**. Click **+ Table**. A QR code is generated automatically for each table." },
          { q: "How does QR ordering work?", a: "Each table has a unique QR. Customer scans it, the digital menu opens, they choose products and place the order. It goes straight to the kitchen." }
        ]
      },
      {
        id: "ai",
        icon: Bot,
        title: "Artificial Intelligence",
        color: "#CDA34F",
        items: [
          { q: "How to use AI for menu creation?", a: "When adding a product, click the **AI** button. It auto-generates description, professional photo, and price suggestions." },
          { q: "How does the 24/7 AI Agent work?", a: "The **AI Assistant** answers questions, helps with settings and gives suggestions. Works 24 hours. **Your customers can also use it from the digital menu!**" }
        ]
      },
      {
        id: "support",
        icon: Sparkles,
        title: "Integrations & Support",
        color: "#a855f7",
        items: [
          { q: "How to integrate WhatsApp?", a: "Go to **Integrations > WhatsApp**. Scan the QR with your phone. Orders are sent automatically to customers." },
          { q: "Need help?", a: "Contact us at **suporte@kaisosystem.com** or via **WhatsApp**." }
        ]
      }
    ]
  }
};

function GuideSection({ section, isOpen, onToggle }) {
  const Icon = section.icon;
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden bg-white" data-testid={`guide-section-${section.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${section.color}15` }}>
          <Icon className="w-5 h-5" style={{ color: section.color }} />
        </div>
        <span className="font-bold text-sm text-gray-900 flex-1 text-left">{section.title}</span>
        <span className="text-xs text-gray-400 mr-2">{section.items.length}</span>
        {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {isOpen && (
        <div className="border-t border-gray-50">
          {section.items.map((item, i) => (
            <GuideItem key={i} item={item} index={i} color={section.color} />
          ))}
        </div>
      )}
    </div>
  );
}

function GuideItem({ item, index, color }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${index > 0 ? "border-t border-gray-50" : ""}`} data-testid={`guide-item-${index}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50/50 transition-colors text-left">
        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-black text-white" style={{ backgroundColor: color }}>
          {index + 1}
        </div>
        <span className="text-sm text-gray-800 font-medium flex-1">{item.q}</span>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-300" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-300" />}
      </button>
      {open && (
        <div className="px-5 pb-4 pl-14">
          <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html: item.a
                .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900">$1</strong>')
                .replace(/\n/g, '<br/>')
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function GuiaPage() {
  const { user } = useAuth();
  const lang = getLanguageFromLocale();
  const t = T[lang] || T.pt;
  const [openSections, setOpenSections] = useState(new Set([t.sections[0]?.id]));

  const toggleSection = (id) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenSections(new Set(t.sections.map(s => s.id)));
  const collapseAll = () => setOpenSections(new Set());

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="guia-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#CDA34F]/10 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-[#CDA34F]" />
            </div>
            <div>
              <h1 className="text-xl font-extrabold text-gray-900" data-testid="guia-title">{t.title}</h1>
              <p className="text-xs text-gray-500">{t.subtitle}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={expandAll} className="text-[10px] text-gray-400 hover:text-[#CDA34F] font-bold uppercase" data-testid="guia-expand-all">
              {lang === "en" ? "Expand All" : lang === "es" ? "Abrir Todo" : "Abrir Tudo"}
            </button>
            <span className="text-gray-200">|</span>
            <button onClick={collapseAll} className="text-[10px] text-gray-400 hover:text-[#CDA34F] font-bold uppercase" data-testid="guia-collapse-all">
              {lang === "en" ? "Collapse" : lang === "es" ? "Cerrar" : "Fechar"}
            </button>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-3">
          {t.sections.map(section => (
            <GuideSection
              key={section.id}
              section={section}
              isOpen={openSections.has(section.id)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>

        {/* Support CTA */}
        <div className="mt-8 bg-gray-900 rounded-2xl p-6 text-center" data-testid="guia-support">
          <Bot className="w-8 h-8 text-[#CDA34F] mx-auto mb-3" />
          <p className="text-white font-bold text-sm mb-1">
            {lang === "en" ? "Still have questions?" : lang === "es" ? "Aun tienes dudas?" : "Ainda tem duvidas?"}
          </p>
          <p className="text-gray-400 text-xs mb-4">
            {lang === "en" ? "Talk to our 24/7 AI Assistant or contact support" : lang === "es" ? "Habla con nuestro Asistente IA 24/7 o contacta soporte" : "Fale com nosso Assistente IA 24/7 ou entre em contato"}
          </p>
          <div className="flex items-center justify-center gap-3">
            <a href="/ai-assistant?mode=help" className="text-xs font-bold text-[#CDA34F] hover:underline" data-testid="guia-ai-link">
              {lang === "en" ? "AI Assistant" : lang === "es" ? "Asistente IA" : "Assistente IA"}
            </a>
            <span className="text-gray-600">|</span>
            <a href="mailto:grupokaiso@yahoo.com" className="text-xs font-bold text-[#CDA34F] hover:underline" data-testid="guia-email-link">
              suporte@kaisosystem.com
            </a>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
